import { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { register as authRegister } from '../utils/auth';

export const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const validateToken = async (token) => {
        try {
            if (!token) return null;
            // همیشه از سرور validate می‌کنیم تا is_admin تازه باشد (نه کشِ localStorage)
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/validate-token`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data; // {valid, user: {..., is_admin}}
        } catch (error) {
            console.error('Token validation error:', error);
            // آفلاین/خطا → fallback به user ذخیره‌شده
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                return { valid: true, user: JSON.parse(storedUser) };
            }
            return null;
        }
    };

    const persistUser = (u) => {
        // نگه‌داشتن user (با is_admin) در state و localStorage
        const safe = u || null;
        setUser(safe);
        if (safe) localStorage.setItem('user', JSON.stringify(safe));
        else localStorage.removeItem('user');
        return safe;
    };

    const getToken = () => {
        return localStorage.getItem('token');
    };

    const login = async (username, password, googleResponse = null) => {
        try {
            setIsLoading(true);
            let response;

            if (googleResponse) {
                response = { data: googleResponse };
            } else {
                response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
                    username,
                    password
                });
            }

            if (response.data && response.data.access_token) {
                const accessToken = response.data.access_token;
                localStorage.setItem('token', accessToken);
                axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
                
                const userData = await validateToken(accessToken);
                if (userData) {
                    persistUser(userData.user || userData);
                    setToken(accessToken);
                    setIsAuthenticated(true);
                    return { success: true, data: response.data };
                }
            }

            throw new Error('خطا در دریافت توکن');
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
        delete axios.defaults.headers.common['Authorization'];
    };

    const register = async (username, email, password) => {
        try {
            setIsLoading(true);
            const response = await authRegister(username, email, password);
            
            if (response && response.access_token) {
                const accessToken = response.access_token;
                localStorage.setItem('token', accessToken);
                axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
                
                const userData = await validateToken(accessToken);
                if (userData) {
                    persistUser(userData);
                    setToken(accessToken);
                    setIsAuthenticated(true);
                    return { success: true, data: response };
                }
            }
            
            throw new Error('خطا در ثبت‌نام');
        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const initAuth = async () => {
            setIsLoading(true);
            const token = localStorage.getItem('token');
            
            if (token) {
                try {
                    const userData = await validateToken(token);
                    if (userData && userData.valid) {
                        persistUser(userData.user);
                        setToken(token);
                        setIsAuthenticated(true);
                        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    } else {
                        logout();
                    }
                } catch (error) {
                    console.error('Auth initialization error:', error);
                    logout();
                }
            } else {
                setIsAuthenticated(false);
            }
            setIsLoading(false);
        };

        initAuth();
    }, []);

    const value = {
        user,
        token,
        isLoading,
        isAuthenticated,
        login,
        logout,
        getToken,
        register
    };

    return (
        <AuthContext.Provider value={value}>
            {isLoading ? (
                <div className="flex items-center justify-center min-h-screen">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
            ) : (
                children
            )}
        </AuthContext.Provider>
    );
};
